/**
 * PURE. The admin order cost/profit arithmetic, in CENTS. (card 28329)
 *
 * ⛔ THE FOUR-COLUMN RULE. `public.orders` stores FOUR admin-entered cost columns and every
 * backend surface sums all FOUR:
 *   · `api/transformers/orderTransformer.js`        — `deriveOrderCosts`, `transformOrderToListItem`
 *   · `api/services/revenueReconciliationService.js` — `COST_COLUMNS`
 *   · `api/repositories/orderEconomicsRepository.js`, `api/scripts/economics/backfillGate.js`
 *   · `api/services/external/telegramService.js`
 * `OrderDetailPage`'s `CostsCard` re-implemented that sum with only THREE, dropping
 * `shipping_and_handling_cost`. It therefore rendered a Total Cost short by the shipping line
 * and a Profit OVERSTATED by exactly that amount, on every order carrying one.
 *
 * ⛔ Do NOT re-inline this sum in a page. Add a column to `ORDER_COST_KEYS` and the page
 * inherits it. Guarded by `tests/unit/orderCostMath.test.js`.
 *
 * ⛔ THE UNIT IS DECLARED, NEVER GUESSED. (card 28334)
 * This module used to infer the unit of `orders.total` from its magnitude
 * (`total > 999 ? total : total * 100`). That heuristic was measured wrong on live data:
 * over the 345 non-zero `orders.total` rows the median is 12000 (= $120.00), so the column is
 * unambiguously integer CENTS — and the guess multiplied every order under $10.00 by 100.
 * Two production orders (LEGACY-75 `total=570`, LEGACY-66 `total=560`) rendered revenue
 * $570.00/$560.00 and a POSITIVE profit of +$540.00/+$527.90 against a real
 * `consultation_cost` of 3000, when both orders in fact LOST money (−$24.30 / −$26.50).
 * A 100× overstatement is noticeable; a flipped profit sign is not.
 * ⛔ Never reintroduce a magnitude test here. If a new column arrives in dollars, convert it at
 * its own boundary — do not teach this module to guess.
 */

/**
 * The four stored cost columns, as [camelCase, snake_case] pairs.
 * Dual-case per `STANDARDS.md`: repository rows are camelized by `toCamelCaseKeys`, but
 * `GET /api/admin/orders/:id/detail` returns the RAW snake_case row.
 */
export const ORDER_COST_KEYS = Object.freeze([
  Object.freeze(['consultationCost', 'consultation_cost']),
  Object.freeze(['medicationCost', 'medication_cost']),
  Object.freeze(['miscCost', 'misc_cost']),
  Object.freeze(['shippingAndHandlingCost', 'shipping_and_handling_cost'])
]);

/** PURE. Coerce one value to cents; anything non-finite degrades to 0. */
export const toCents = (value) => {
  const cents = Number(value);
  return Number.isFinite(cents) ? cents : 0;
};

/** PURE. Dual-case read of a single cost column, in cents. */
export const readCostCents = (order, camelKey, snakeKey) =>
  toCents(order?.[camelKey] ?? order?.[snakeKey]);

/** PURE. Sum a list of cent values, ignoring non-numeric entries. */
export const sumCostCents = (values = []) =>
  values.reduce((total, value) => total + toCents(value), 0);

/** PURE. Total of ALL FOUR stored cost columns on an order row, in cents. */
export const calculateOrderCostCents = (order) =>
  sumCostCents(ORDER_COST_KEYS.map(([camelKey, snakeKey]) => readCostCents(order, camelKey, snakeKey)));

/** The text a money field shows when the underlying value is absent or unreadable. */
export const UNRESOLVED_MONEY_LABEL = '—';

/**
 * PURE. Format an integer number of CENTS as USD. The unit is declared by the name, so this
 * function never inspects the magnitude of its argument.
 *
 * An absent or non-finite amount is NAMED as unresolved rather than invented as `$0.00` —
 * the same rule `web/utils/monthlyCloseoutModel.js` applies, so the two surfaces agree.
 *
 * ⛔ Do NOT use this for `order_economics` / `order_cost_snapshots`, whose money columns are
 * `numeric(12,2)` DOLLARS. See `web/components/admin/OrderEconomicsPanel.jsx`.
 */
export const formatCentsUsd = (cents) => {
  if (cents == null) return UNRESOLVED_MONEY_LABEL;
  const value = Number(cents);
  if (!Number.isFinite(value)) return UNRESOLVED_MONEY_LABEL;
  // Sign before the symbol (-$24.30), matching `formatCloseoutMoney` in
  // `web/utils/monthlyCloseoutModel.js`, so the two money surfaces read the same.
  // ⛔ Deliberately NO thousands separator: the amounts this replaces rendered as
  // `$1200.00`, and card 28334 must leave every order above $9.99 byte-identical.
  return `${value < 0 ? '-' : ''}$${(Math.abs(value) / 100).toFixed(2)}`;
};

/**
 * PURE. Revenue in cents. `orders.total` IS cents — read it, do not guess it.
 * See the unit note in the module header before changing this line.
 */
export const resolveRevenueCents = (order) => toCents(order?.total);

/** PURE. Profit in cents: revenue minus ALL FOUR cost columns. */
export const calculateOrderProfitCents = (order) =>
  resolveRevenueCents(order) - calculateOrderCostCents(order);
